import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssignUserToTerminalDto, PlaceInListDto, TenantUsersApi, TenantUsersDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ProblemDetail, useModalErrorHandler } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-assign-bus-stop-manager',
  imports: [
    CommonModule,
    NzModalModule,
    ReactiveFormsModule,
    NzFormModule,
    NzSelectModule,
    NzButtonModule,
    NzSpinComponent,
    NzInputModule,
    ErrorDisplayComponent,
  ],
  standalone: true,
  template: `
    <nz-modal
      (nzOnCancel)="onHide()"
      (nzOnOk)="onOk()"
      [nzClosable]="true"
      [nzOkDisabled]="form.invalid || isSubmitting() "
      [nzVisible]="show"
      [nzWidth]="500"
      [nzOkLoading]="isSubmitting()"
      nzOkText="Assign"
    >
      <div *nzModalTitle class="font-semibold">Assign Bus Stop Manager</div>
      <div *nzModalContent>
        <nz-spin [nzSpinning]="isSubmitting()">
          <app-error-display
            *ngIf="showBusStopError()"
            [error]="busStopErrorDetails"
            [showRetry]="true"
            (retry)="onRetryBusStop()"
            (dismiss)="onDismissBusStopError()"
          ></app-error-display>
          <form [formGroup]="form" class="grid grid-cols-1 gap-4" nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label [nzRequired]="true">Select Bus Stop Manager</nz-form-label>
              <nz-form-control>
                <nz-select
                  [nzLoading]="busStopManagersQuery.isFetching()"
                  [nzDisabled]="busStopManagersQuery.isFetching()"
                  formControlName="busStopManagerId"
                  nzNotFoundContent="No assignable bus stop managers. You may have to add a new bus stop manager user"
                  [nzPlaceHolder]="busStopManagersQuery.isFetching() ? 'Loading assignable managers...' :'Choose a bus stop manager'"
                >
                  <nz-option
                    *ngFor="let manager of busStopManagers"
                    [nzLabel]="manager.name"
                    [nzValue]="manager.id"
                  ></nz-option>
                </nz-select>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label>Notes (Optional)</nz-form-label>
              <nz-form-control>
                <textarea
                  nz-input
                  formControlName="notes"
                  rows="3"
                  placeholder="Add any notes about this assignment..."
                ></textarea>
              </nz-form-control>
            </nz-form-item>
          </form>
        </nz-spin>
      </div>
    </nz-modal>
  `,
})
export class AssignBusStopManagerComponent implements OnInit, OnChanges {
  private notification = inject(NzNotificationService);
  private fb = inject(FormBuilder);
  private tenantUsersApi = inject(TenantUsersApi);
  private modalErrorHandler = useModalErrorHandler();

  @Input() show = false;
  busStop = input.required<PlaceInListDto>();
  @Output() hide = new EventEmitter(true);
  @Output() assignmentCompleted = new EventEmitter<void>();

  form!: FormGroup;
  isSubmitting = signal(false);

  // Error state management
  showBusStopError = signal(false);
  busStopErrorDetails: ProblemDetail | null = null;

  busStopManagersQuery = injectQuery(() => ({
    queryKey: ['bus-stop-managers', this.busStop()?.id],
    queryFn: () =>
      lastValueFrom(
        this.tenantUsersApi.tenantUsersControllerGetAssignableBusStopManagersForBusStop({
          busStopId: this.busStop()?.id || '',
        }),
      ),
    enabled: !!this.busStop()?.id,
    retry: 1,
  }));

  get busStopManagers() {
    return this.busStopManagersQuery.data() ?? [];
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show'] && this.show) {
      this.resetForm();
    }
  }

  private initForm() {
    this.form = this.fb.group({
      busStopManagerId: ['', [Validators.required]],
      notes: [''],
      forceReplace: [true],
    });
  }

  private resetForm() {
    this.form.reset({
      busStopManagerId: '',
      notes: '',
      forceReplace: true,
    });
    this.isSubmitting.set(false);
  }

  onHide() {
    this.resetForm();
    this.hide.emit();
  }

  onOk() {
    if (this.form.valid && !this.isSubmitting()) {
      this.isSubmitting.set(true);
      const formValue = this.form.value;
      const selectedManager = this.busStopManagers.find((u: TenantUsersDto) => u.id === formValue.busStopManagerId);
      if (!selectedManager) {
        this.notification.error('No Bus Stop Manager Selected', 'Please select a bus stop manager.');
        this.isSubmitting.set(false);
        return;
      }
      const payload: AssignUserToTerminalDto = {
        userId: selectedManager.id,
        terminalId: this.busStop().id,
        notes: formValue.notes || undefined,
        forceReplace: true,
      };

      // Using lastValueFrom instead of the deprecated toPromise
      lastValueFrom(
        this.tenantUsersApi.tenantUsersControllerAssignUserToTerminal({ id: selectedManager.id, body: payload }),
      )
        .then(() => {
          this.notification.success(
            '✅ Bus Stop Manager Assigned',
            `${selectedManager.firstName} ${selectedManager.surname} has been successfully assigned as Bus Stop Manager to this bus stop.`,
          );
          this.assignmentCompleted.emit();
          this.onHide();
        })
        .catch((error) => {
          const result = this.modalErrorHandler.handleModalError(error, {
            modalName: 'assign-bus-stop-manager',
            action: 'assign-bus-stop-manager',
          });
          if (!result.shouldHandleGlobally) {
            this.notification.error('❌ Assignment Failed', result.userMessage);
          }

          this.onHide();
        })
        .finally(() => {
          // Only set isSubmitting to false in the finally block
          this.isSubmitting.set(false);
        });
    }
  }

  // Error handling methods
  onRetryBusStop() {
    this.showBusStopError.set(false);
    this.busStopManagersQuery.refetch();
  }

  onDismissBusStopError() {
    this.showBusStopError.set(false);
  }
}
